/**
 * API Response Types
 *
 * Why: Type-safe Api response handling
 * What: Interfaces matchin your backend api response format
 * Benefits:
 *  - Catches Api contract changes at compile time
 *  - Self-documenting API expectations
 *  - Consistent error parsing
 *
 * Note: Adjust these to match your backend API response format
 */

import type { PaginationMeta } from "@/types";

/**
 * Base API response structure
 * ALL API responses should follow this format
 */

export interface ApiResponseBase {
  /** Whether request was successful */
  success: "success" | "fail";
  message: string;
  Timestamp?: string;
  requestId?: string;
}

/**
 * Successful Api response
 * Contains the requested data
 */
export interface ApiSuccessResponse<T = unknown> extends ApiResponseBase {
  success: "success";
  /** Response data */
  data: T;
  /** Pagination metadata (if applicable) */
  meta?: PaginationMeta;
}

/**
 * Failed API response
 * contains error information
 *
 */

export interface ApiErrorResponse extends ApiResponseBase {
  success: "fail";
  message: string;
  statusCode: number;
  /** Application error code */
  code?: string;
  /** Field-level validation errors */
  errors?: Record<string, string[]>;
  /** Api endpoint that failed */
  path?: string;
}

/**
 * Type guard to check if response is an error
 */
export const isApiErrorResponse = (
  response: unknown,
): response is ApiErrorResponse => {
  return (
    typeof response === "object" &&
    response !== null &&
    "success" in response &&
    (response as ApiResponseBase).success === "fail"
  );
};

/**
 * Type guard to check if response is an successful
 */
export const isApiSuccessResponse = <T>(
  response: unknown,
): response is ApiSuccessResponse<T> => {
  return (
    typeof response === "object" &&
    response !== null &&
    "success" in response &&
    (response as ApiResponseBase).success === "success"
  );
};
